import ClearIcon from '@mui/icons-material/Clear'
import { IconButton, InputAdornment, InputAdornmentProps, Stack, useTheme } from '@mui/material'
import { DatePickerSlots, DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import type { PickersTextFieldProps } from '@mui/x-date-pickers/PickersTextField'
import { PickerValue } from '@mui/x-date-pickers/internals'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { FieldProps, getIn, useFormikContext } from 'formik'
import { FocusEvent } from 'react'
import { ExtendedTheme } from '../../../../theme'
import { createErrorHandler } from './errorHandler'

dayjs.extend(customParseFormat)

export interface DatePickerProps
  extends FieldProps,
    Omit<
      PickersTextFieldProps<'standard'>,
      'name' | 'error' | 'InputProps' | 'onChange' | 'variant' | 'defaultValue' | 'value'
    > {
  inlineClearButton?: boolean
  ciboSourced?: boolean
  defaultValue?: PickerValue
  value?: PickerValue
  onChange: (date: PickerValue | null) => void
  stringValue?: string
  slots: DatePickerSlots
}

export function DatePicker(props: DatePickerProps) {
  const {
    field: { onChange: _onChange, value, ...field },
    form,
    inlineClearButton,
    disabled,
    ciboSourced,
    label,
    onChange,
    onError,
    stringValue,
    slots,
    ...rest
  } = props
  const { isSubmitting, setFieldError } = form
  const { setFieldValue, setFieldTouched, touched, errors } = useFormikContext<any>()
  const fieldError = getIn(errors, field.name)
  const theme = useTheme<ExtendedTheme>()
  const showError = getIn(touched, field.name) && !!fieldError

  const textFieldProps: Partial<PickersTextFieldProps> = {
    error: showError as any,
    helperText: showError ? fieldError : null,
    label,
    size: 'small',
    sx: { background: ciboSourced ? theme.palette.ciboSensed : undefined },
    onBlur: (event: FocusEvent<HTMLDivElement, Element>) => {
      setFieldTouched(field.name, true, true)
    },
  }

  const datePickerSlots = {
    inputAdornment: (props: InputAdornmentProps) => (
      <Stack direction="row" alignItems="center">
        <InputAdornment
          position="end"
          sx={{
            marginRight: '10px',
            visibility: inlineClearButton ? 'visible' : 'hidden',
            transition: 'opacity 0.2s',
            opacity: 0,
            '.Mui-focused &, &:hover &, .Mui-focused, &:hover': {
              opacity: 1,
            },
            // Show on parent hover/focus
            '.MuiInputBase-root:hover &, .MuiInputBase-root.Mui-focused &, .MuiPickersInputBase-root:hover &':
              {
                opacity: 1,
              },
          }}
        >
          <IconButton
            size="small"
            onClick={() => setFieldValue(field.name, null, true)}
            sx={{ marginRight: '-14px' }}
            tabIndex={-1}
          >
            <ClearIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </InputAdornment>
        {props.children}
      </Stack>
    ),
  }

  const finalValue = value ? (stringValue ? dayjs(value).toDate() : new Date(value)) : null

  return (
    <MuiDatePicker
      slotProps={{ textField: textFieldProps }}
      disabled={disabled ?? isSubmitting}
      onError={createErrorHandler(fieldError, field.name, setFieldError)}
      value={finalValue}
      {...field}
      slots={datePickerSlots}
      onChange={
        onChange ??
        ((date: PickerValue | undefined) => {
          // Do not switch this order, otherwise you might cause a race condition
          // See https://github.com/formium/formik/issues/2083#issuecomment-884831583
          setFieldTouched(field.name, true, false)

          if (stringValue) {
            const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : null
            setFieldValue(field.name, formattedDate, true)
          } else {
            setFieldValue(field.name, date, true)
          }
        })
      }
      {...rest}
    />
  )
}

DatePicker.displayName = 'DatePickerField'
